import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentPaymentTask } from '@/providers/PaymentTaskProvider';
import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { toastApiError } from '@/lib/toast';
import { useParams } from 'react-router-dom';
import { InputPrice } from '@/components/element/Form/InputPrice';
import { useUpdateOrderAllocatedExpense } from '@/hooks/api/1194_update_order_allocated_expense';
import { useUpdateNonAllocableAmount } from '@/hooks/api/1191_update_non_allocable_amount';
import { useRelatedOrders } from '@/hooks/api/0640_related_orders';

type NonAllocatedExpenseFormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  task_id: number;
  non_allocable_amount: number;
};

export const NonAllocatedExpenseRow = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task, setPaymentTask } = useCurrentPaymentTask();
  const [editing, setEditing] = useState<boolean>(false);
  const { mutate, data, isSuccess, error, isPending } = useUpdateNonAllocableAmount();
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NonAllocatedExpenseFormInputs>({
    mode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      task_id: task!.id,
      non_allocable_amount: task!.non_allocable_amount,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onSubmit: SubmitHandler<NonAllocatedExpenseFormInputs> = (inputData) => {
    setShowLoading(true);
    mutate(inputData);
  };

  useEffect(() => {
    if (isSuccess && data) {
      setEditing(false);
      setPaymentTask(data);
    }
  }, [isSuccess]);

  return (
    <tr>
      <td>
        <div className="d-flex align-items-center">
          <div className="icon icon-sm bg-dark d-flex border-radius-md align-items-center justify-content-center flex-shrink-0">
            <i className="fas fa-yen-sign text-white"></i>
          </div>
          <div className="ms-2">
            <div className="font-weight-bold px-0 text-sm text-dark mb-1">按分対象外費用</div>
          </div>
        </div>
      </td>
      {editing && (
        <>
          <td className="text-end text-dark text-xs">
            <div className="input-group">
              <Controller
                render={({ field: { onChange, value, ...field } }) => (
                  <InputPrice onChange={onChange} value={value} error={errors.non_allocable_amount} />
                )}
                control={control}
                name="non_allocable_amount"
                rules={{ required: true }}
              />
            </div>
          </td>
          <td className="text-end">
            <button className="btn btn-icon-only btn-outline-secondary me-1" onClick={() => setEditing(false)}>
              <i className="fas fa-undo"></i>
            </button>
            <button className="btn btn-icon-only bg-gradient-dark" onClick={() => handleSubmit(onSubmit)()}>
              <i className="fas fa-check-circle"></i>
            </button>
          </td>
        </>
      )}
      {!editing && (
        <>
          <td className="text-end text-dark text-xs">¥{task!.non_allocable_amount.toLocaleString()}</td>
          <td className="text-end">
            <button className="btn btn-icon-only bg-gradient-success" onClick={() => setEditing(true)}>
              <i className="fas fa-edit"></i>
            </button>
          </td>
        </>
      )}
    </tr>
  );
};
